import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Stack, Chip, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Button, Breadcrumbs, Link,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText, TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TuneIcon from '@mui/icons-material/Tune';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import BuildIcon from '@mui/icons-material/Build';
import UndoIcon from '@mui/icons-material/Undo';
import { useParams, useNavigate } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { useReadOnly } from '../../context/ReadOnlyContext';
import { useAuthStore } from '../../store/authStore';

const DESCRIPTION_FORMAT_LABELS = {
  plain: 'Plain text', markdown: 'Markdown', html: 'HTML', rtf: 'RTF',
};

const UNBUILD_ROLES = ['admin', 'manager', 'owner'];

function Field({ label, value }) {
  return (
    <Box sx={{ py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 160, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value ?? '—'}</Typography>
    </Box>
  );
}

const ACTION_COLOR = {
  CREATE: 'success', ADJUST: 'primary', UPDATE: 'default',
  DELETE: 'error', TRANSFER: 'secondary', STATUS_CHANGE: 'warning',
  BUILD: 'success', UNBUILD: 'warning',
};

const STATUS_CHIP = {
  active: { label: 'Active', color: 'success' },
  inactive: { label: 'Inactive', color: 'warning' },
  pending_deletion: { label: 'Pending Deletion', color: 'error' },
};

export default function InventoryDetailPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { isReadOnly } = useReadOnly();
  const storeRole = useAuthStore((s) => s.role);
  const [profileRole, setProfileRole] = useState(null);
  const canUnbuild = UNBUILD_ROLES.includes(profileRole || storeRole);

  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [refItems, setRefItems] = useState({}); // id → item, for bundle components + build item
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [features, setFeatures] = useState({});

  // Action dialogs
  const [adjust, setAdjust] = useState(null); // { delta, reason }
  const [adjusting, setAdjusting] = useState(false);
  const [build, setBuild] = useState(null); // { unbuild, buildItemObj, qty }
  const [building, setBuilding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const buildEnabled = features['inventory.bundle_assembly'] || false;
  const bundleEnabled = features['inventory.bundle_products'] || false;

  const load = () => {
    setLoading(true);
    Promise.all([
      tenantApi.getInventoryItem(itemId),
      tenantApi.getInventoryHistory(itemId, { limit: 20 }),
    ])
      .then(async ([i, h]) => {
        setItem(i.item);
        setHistory(h.history || []);
        // For bundles, resolve linked component + build item details for display.
        const it = i.item;
        if (it?.type === 'bundle') {
          const ids = [...(it.components || []).map((c) => c.itemId), it.buildItemId].filter(Boolean);
          const fetched = await Promise.all([...new Set(ids)].map((id) =>
            tenantApi.getInventoryItem(id).then((r) => r.item).catch(() => null)
          ));
          const map = {};
          fetched.forEach((f) => { if (f) { map[f.itemId] = f; } });
          setRefItems(map);
        } else {
          setRefItems({});
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [itemId]);

  useEffect(() => {
    tenantApi.getMyFeatures().then((d) => setFeatures(d.features || d || {})).catch(() => {});
    tenantApi.getMyProfile().then((d) => setProfileRole(d.user?.role || null)).catch(() => {});
  }, []);

  const notify = (message, severity = 'success') => setToast({ message, severity });

  const handleStatus = async (newStatus) => {
    try {
      await tenantApi.updateItemStatus(itemId, newStatus);
      notify(`Item ${newStatus === 'active' ? 'activated' : newStatus === 'inactive' ? 'deactivated' : 'updated'}.`);
      load();
    } catch (e) { notify(e.message, 'error'); }
  };

  const handleAdjust = async () => {
    const delta = parseFloat(adjust.delta);
    if (isNaN(delta) || delta === 0) { return; }
    setAdjusting(true);
    try {
      await tenantApi.adjustInventory(itemId, delta, adjust.reason);
      notify(`Stock adjusted by ${delta > 0 ? '+' : ''}${delta}.`);
      setAdjust(null);
      load();
    } catch (e) { notify(e.message, 'error'); }
    setAdjusting(false);
  };

  const openBuild = async (unbuild) => {
    // Fetch the build item so we can show its name + on-hand for the max guard.
    let buildItemObj = null;
    if (item.buildItemId) {
      try { buildItemObj = (await tenantApi.getInventoryItem(item.buildItemId)).item; } catch { /* best effort */ }
    }
    setBuild({ unbuild, buildItemObj, qty: '' });
  };

  const handleBuild = async () => {
    const qty = parseInt(build.qty, 10);
    if (!Number.isInteger(qty) || qty <= 0) { return; }
    setBuilding(true);
    try {
      await tenantApi.buildBundle(itemId, qty, { unbuild: build.unbuild });
      notify(build.unbuild ? `Unbuilt ${qty} × ${item.name}.` : `Built ${qty} × ${item.name}.`);
      setBuild(null);
      load();
    } catch (e) { notify(e.message, 'error'); }
    setBuilding(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await tenantApi.deleteInventoryItem(itemId);
      notify('Item marked for deletion (7-day hold).');
      setConfirmDelete(false);
      load();
    } catch (e) { notify(e.message, 'error'); }
    setDeleting(false);
  };

  if (loading) { return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>; }
  if (error) { return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>; }
  if (!item) { return null; }

  const status = item.status || 'active';
  const isBundle = item.type === 'bundle';
  const sc = STATUS_CHIP[status] || STATUS_CHIP.active;

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton size="small" onClick={() => navigate('/inventory')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Breadcrumbs>
          <Link component="button" variant="body2" onClick={() => navigate('/inventory')}>Inventory</Link>
          <Typography variant="body2" color="text.primary">{item.name}</Typography>
        </Breadcrumbs>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{item.name}</Typography>
          <Stack direction="row" spacing={1} mt={0.5}>
            <Chip label={`SKU: ${item.sku}`} size="small" variant="outlined" />
            <Chip
              label={`Qty: ${item.quantity}`}
              size="small"
              color={item.quantity <= 0 ? 'error' : item.quantity <= 10 ? 'warning' : 'success'}
              variant={isBundle ? 'outlined' : 'filled'}
            />
            {isBundle && <Chip label="Bundle" size="small" color="secondary" variant="outlined" />}
            <Chip label={item.category} size="small" />
            <Chip label={sc.label} size="small" color={sc.color} variant="outlined" />
          </Stack>
        </Box>

        {/* Action icons — mirror the inventory list row, functional in place */}
        <Stack direction="row" spacing={0.5}>
          {status === 'active' && (
            <>
              {/* Bundles can't be edited while the Inventory Bundles feature is off. */}
              <Tooltip title={isBundle && !bundleEnabled
                ? 'Enable the Inventory Bundles add-on to edit this bundle.'
                : 'Edit item'}>
                <span>
                  <IconButton onClick={() => navigate('/inventory', { state: { editItemId: item.itemId } })}
                    disabled={isReadOnly || (isBundle && !bundleEnabled)}>
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Standard items adjust stock; bundles build/unbuild (computed stock) */}
              {!isBundle && (
                <Tooltip title="Adjust stock">
                  <span>
                    <IconButton color="primary" onClick={() => setAdjust({ delta: '', reason: '' })} disabled={isReadOnly}>
                      <TuneIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {isBundle && (buildEnabled || item.buildItemId) && (
                <Tooltip title={!buildEnabled
                  ? 'Enable the Build Bundles feature to build bundles.'
                  : (item.buildItemId ? 'Build (assemble into build item)' : 'Set a Build Item on this bundle to enable building')}>
                  <span>
                    <IconButton color="success" onClick={() => openBuild(false)} disabled={isReadOnly || !buildEnabled || !item.buildItemId}>
                      <BuildIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {isBundle && canUnbuild && (buildEnabled || item.buildItemId) && (
                <Tooltip title={!buildEnabled
                  ? 'Enable the Build Bundles feature to unbuild bundles.'
                  : (item.buildItemId ? 'Unbuild (disassemble into components)' : 'Set a Build Item on this bundle to enable unbuilding')}>
                  <span>
                    <IconButton color="warning" onClick={() => openBuild(true)} disabled={isReadOnly || !buildEnabled || !item.buildItemId}>
                      <UndoIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              <Tooltip title="Mark inactive">
                <span>
                  <IconButton onClick={() => handleStatus('inactive')} disabled={isReadOnly}><BlockIcon color="warning" /></IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Mark for deletion">
                <span>
                  <IconButton onClick={() => setConfirmDelete(true)} disabled={isReadOnly}><DeleteIcon color="error" /></IconButton>
                </span>
              </Tooltip>
            </>
          )}

          {status === 'inactive' && (
            <>
              <Tooltip title="Activate">
                <span>
                  <IconButton color="success" onClick={() => handleStatus('active')} disabled={isReadOnly}><CheckCircleIcon /></IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Mark for deletion">
                <span>
                  <IconButton onClick={() => setConfirmDelete(true)} disabled={isReadOnly}><DeleteIcon color="error" /></IconButton>
                </span>
              </Tooltip>
            </>
          )}

          {status === 'pending_deletion' && (
            <Tooltip title="Cancel deletion">
              <span>
                <IconButton color="primary" onClick={() => handleStatus('active')} disabled={isReadOnly}><RestoreIcon /></IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {status === 'pending_deletion' && item.deletionScheduledAt && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Scheduled for permanent deletion on {new Date(item.deletionScheduledAt).toLocaleDateString()}.
          This item may be referenced in reports or external integrations.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Item details */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Item Details</Typography>
            <Field label="Item ID"      value={item.itemId} />
            <Field label="Name"         value={item.name} />
            <Field label="SKU"          value={item.sku} />
            {/* Flattened text — never inject stored markup into the DOM. */}
            <Field
              label="Description"
              value={item.descriptionText || item.description}
            />
            <Field
              label="Description Format"
              value={DESCRIPTION_FORMAT_LABELS[item.descriptionFormat] || 'Plain text'}
            />
            <Field label="Category"     value={item.category} />
            <Field label="Unit"         value={item.unit} />
            <Field label="Warehouse"    value={item.warehouseId} />
            <Field label="Type"         value={isBundle ? 'Bundle' : 'Item'} />
            {isBundle && (
              <Field label="Build Item" value={item.buildItemId
                ? (refItems[item.buildItemId] ? `${refItems[item.buildItemId].name} (${refItems[item.buildItemId].sku})` : item.buildItemId)
                : 'Not set'} />
            )}
            <Field label="Quantity"     value={item.quantity} />
            <Field label="Cost Price"   value={item.costPrice !== null && item.costPrice !== undefined ? `$${Number(item.costPrice).toFixed(2)}` : null} />
            <Field label="Sale Price"   value={item.salePrice !== null && item.salePrice !== undefined ? `$${Number(item.salePrice).toFixed(2)}` : null} />
            <Field label="Status"       value={sc.label} />
            {item.serialNumber && <Field label="Serial #" value={item.serialNumber} />}
            {item.lotNumber && <Field label="Lot #" value={item.lotNumber} />}
            {item.expiryDate && <Field label="Expiry Date" value={item.expiryDate} />}
            <Field label="Last Updated" value={new Date(item.updatedAt).toLocaleString()} />
            <Field label="Created"      value={new Date(item.createdAt).toLocaleString()} />
          </Paper>

          {/* Bundle composition — linked components + build item */}
          {isBundle && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>Bundle Composition</Typography>
              {typeof item.computedStock === 'number' && <Field label="Buildable Now" value={item.computedStock} />}
              {typeof item.computedCost === 'number' && <Field label="Computed Cost" value={`$${Number(item.computedCost).toFixed(2)}`} />}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
                Components (consumed per bundle)
              </Typography>
              {(item.components || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No components linked.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Linked Item</TableCell>
                      <TableCell align="right">Qty / Bundle</TableCell>
                      <TableCell align="right">On Hand</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(item.components || []).map((c) => {
                      const ref = refItems[c.itemId];
                      return (
                        <TableRow key={c.itemId} hover>
                          <TableCell>
                            {ref ? ref.name : c.itemId}
                            {ref && <Typography variant="caption" color="text.secondary" display="block">{ref.sku}</Typography>}
                          </TableCell>
                          <TableCell align="right">{c.qtyPerBundle}</TableCell>
                          <TableCell align="right">{ref ? ref.quantity : '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Paper>
          )}

          {/* Vendor info — only shown if any vendor field populated */}
          {(item.vendorName || item.vendorId || item.vendorAddress || item.vendorPhone || item.vendorContact) && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>Vendor Information</Typography>
              <Field label="Vendor Name"    value={item.vendorName} />
              <Field label="Vendor ID"      value={item.vendorId} />
              <Field label="Vendor Address" value={item.vendorAddress} />
              <Field label="Vendor Phone"   value={item.vendorPhone} />
              <Field label="Vendor Contact" value={item.vendorContact} />
            </Paper>
          )}
        </Grid>

        {/* Change history */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined">
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={600}>Change History</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell align="right">Before</TableCell>
                  <TableCell align="right">Delta</TableCell>
                  <TableCell align="right">After</TableCell>
                  <TableCell>When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No history yet.
                    </TableCell>
                  </TableRow>
                ) : history.map((h, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Chip label={h.action} size="small" color={ACTION_COLOR[h.action] || 'default'} />
                      {h.reason && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {h.reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{h.quantityBefore ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, color: h.delta > 0 ? 'success.main' : h.delta < 0 ? 'error.main' : 'inherit' }}>
                      {h.delta !== null && h.delta !== undefined ? (h.delta > 0 ? `+${h.delta}` : h.delta) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{h.quantityAfter ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {h.performedAt ? new Date(h.performedAt).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      {/* Adjust dialog */}
      <Dialog open={!!adjust} onClose={() => setAdjust(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Stock — {item.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Current quantity: <strong>{item.quantity}</strong>
            </Typography>
            <TextField
              label="Delta (+ to add, - to remove)" size="small" type="number" fullWidth autoFocus
              value={adjust?.delta || ''}
              onChange={(e) => setAdjust((a) => ({ ...a, delta: e.target.value }))}
              helperText={adjust?.delta ? `New quantity: ${item.quantity + parseFloat(adjust.delta || 0)}` : ' '}
            />
            <TextField
              label="Reason" size="small" fullWidth multiline rows={2}
              value={adjust?.reason || ''}
              onChange={(e) => setAdjust((a) => ({ ...a, reason: e.target.value }))}
              placeholder="e.g. Received PO-12345"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjust(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdjust}
            disabled={adjusting || !adjust?.delta || parseFloat(adjust?.delta) === 0}>
            {adjusting ? 'Saving...' : 'Apply Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Build / Unbuild dialog */}
      <Dialog open={!!build} onClose={() => setBuild(null)} maxWidth="xs" fullWidth>
        {build && (() => {
          const buildItemName = build.buildItemObj?.name || item.buildItemId;
          const buildable = typeof item.computedStock === 'number' ? item.computedStock : item.quantity;
          const qtyNum = parseInt(build.qty, 10);
          const max = build.unbuild ? build.buildItemObj?.quantity : buildable;
          const exceedsMax = Number.isInteger(qtyNum) && max !== undefined && qtyNum > max;
          return (
            <>
              <DialogTitle>{build.unbuild ? 'Unbuild' : 'Build'} — {item.name}</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Build Item: <strong>{buildItemName}</strong>
                    {build.buildItemObj && <> (currently {build.buildItemObj.quantity} on hand)</>}
                  </Typography>
                  {!build.unbuild ? (
                    <Alert severity="info">
                      Building consumes the linked components and adds the built quantity to <strong>{buildItemName}</strong>.
                      You can build up to <strong>{buildable}</strong> right now.
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      Unbuilding removes the quantity from <strong>{buildItemName}</strong> and restores the linked components.
                    </Alert>
                  )}
                  <TextField
                    label={`Quantity to ${build.unbuild ? 'unbuild' : 'build'}`}
                    size="small" type="number" fullWidth autoFocus
                    value={build.qty}
                    onChange={(e) => setBuild((b) => ({ ...b, qty: e.target.value }))}
                    error={exceedsMax}
                    helperText={exceedsMax
                      ? (build.unbuild ? `Only ${max} built unit(s) available to unbuild.` : `Only ${max} buildable from current component stock.`)
                      : ' '}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setBuild(null)}>Cancel</Button>
                <Button variant="contained" color={build.unbuild ? 'warning' : 'success'} onClick={handleBuild}
                  disabled={building || !Number.isInteger(qtyNum) || qtyNum <= 0 || exceedsMax}>
                  {building ? 'Working...' : build.unbuild ? 'Unbuild' : 'Build'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="sm">
        <DialogTitle>Mark Item for Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{item.name}</strong> (SKU: {item.sku}) will be marked for deletion and permanently removed after 7 days.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            You can cancel the deletion within the 7-day window.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Processing...' : 'Mark for Deletion'}
          </Button>
        </DialogActions>
      </Dialog>

      {toast && (
        <Alert
          severity={toast.severity}
          onClose={() => setToast(null)}
          sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, boxShadow: 3 }}
        >
          {toast.message}
        </Alert>
      )}
    </Box>
  );
}
